import { useQuery } from "@tanstack/react-query";

import { checkServerReachable } from "./pb";
import { queryKeys } from "./queryKeys";

export type ServerHealth = {
  /** The last probe succeeded. */
  online: boolean;
  /** No probe has completed yet — say "connecting", not "offline". */
  pending: boolean;
  /** A probe is in flight right now. */
  checking: boolean;
  /** Probe again immediately, e.g. from a "Retry" button. */
  recheck: () => void;
};

/**
 * Polls the Kobo server so the shell can tell the user the moment the backend
 * goes away, instead of letting every screen quietly render empty.
 *
 * Note the failure handling: TanStack Query keeps the last successful `data`
 * when a refetch fails, so "is the server up" has to be read from the query's
 * error state. Reading `data` alone leaves the indicator green forever after the
 * first success — exactly the silent-outage failure this hook exists to prevent.
 */
export function useServerHealth(): ServerHealth {
  const query = useQuery({
    queryKey: queryKeys.health,
    async queryFn() {
      await checkServerReachable();
      return true;
    },
    retry: false,
    staleTime: 0,
    gcTime: Infinity,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Keep polling even when the window is in the background: a desktop app
    // parked behind an editor still has to notice that the VPS went away.
    refetchIntervalInBackground: true,
    refetchInterval: (currentQuery) => (currentQuery.state.status === "error" ? 8_000 : 30_000),
  });

  return {
    online: query.status === "success",
    pending: query.status === "pending",
    checking: query.isFetching,
    recheck: () => void query.refetch(),
  };
}
